import React from 'react';
import {
  HStack,
  Text,
  VStack,
  View,
  ChevronSmallDownIcon,
  ChevronSmallRightIcon,
} from '@packages/systems/spring';

type SettingsSectionProps = {
  label: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  /**
   * Controls the bottom divider below the section.
   *
   * - `true`: border always visible, regardless of expanded state.
   * - `false`: border never visible.
   * - `undefined` (default): border visible only when collapsed.
   *
   * Mirrors the `showBottomBorder` prop on `/designer-flux`'s `SectionContainer`,
   * minus the `subsection` branch that `SettingsSection` doesn't model.
   */
  showBottomBorder?: boolean;
  ['aria-label']?: string;
  ['data-automation-id']?: string;
};

/**
 * Collapsible right-sidebar section used by frame settings panels on the Open Canvas.
 *
 * The header displays a label and a chevron that reflects the expanded state.
 * The body renders only while `expanded` is true.
 *
 * This primitive covers the subset of behavior that frame settings panels rely on
 * from `/designer-flux`'s `SectionContainer`, so package-boundary-respecting
 * components (e.g. `MCCFrameSettings`, and eventually `ComponentCanvasFrameSettings`)
 * can reuse it instead of bridging through `/designer-flux`.
 *
 * @temporary This is a stand-in for `/designer-flux`'s `SectionContainer` and should
 * be deleted once `SectionContainer` is relocated into a shared Designer UI package
 * that both `@packages/systems/open-canvas` and `@packages/systems/multi-context-canvas`
 * can import from.
 *
 * Tracked by https://webflow.atlassian.net/browse/DEX-651.
 */
export const SettingsSection: React.FC<SettingsSectionProps> = ({
  label,
  expanded,
  onToggle,
  children,
  showBottomBorder,
  'aria-label': ariaLabel,
  'data-automation-id': dataAutomationId,
}) => {
  const ChevronIcon = expanded ? ChevronSmallDownIcon : ChevronSmallRightIcon;

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggle();
      }
    },
    [onToggle]
  );

  const shouldRenderBorder = showBottomBorder ?? !expanded;

  return (
    <VStack
      spacing={0}
      data-automation-id={dataAutomationId}
      aria-label={ariaLabel}
    >
      <HStack
        align="center"
        justify="space-between"
        paddingX="$2"
        paddingTop="$1"
        paddingBottom="$1"
        marginBottom={expanded ? '$-2' : undefined}
        height="$10"
        background="$background1"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
      >
        <Text font="title" colorVariant="primary">
          {label}
        </Text>
        <View color="$textSecondary" marginRight="$1">
          <ChevronIcon />
        </View>
      </HStack>

      {expanded && <View background="$background1">{children}</View>}
      {shouldRenderBorder && (
        <View width="100%" height="$0.25" borderBottom="$border1" />
      )}
    </VStack>
  );
};
